import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { buildSimduneUrl } from "../utils/simdune.js";
async function fetchSimBalance(address, chainIdsParam) {
  if (!address) throw new Error("Address is required");
  const chainIds = chainIdsParam.length === 0 ? "mainnet" : chainIdsParam.join(",");
  const queryParams = new URLSearchParams();
  queryParams.append("metadata", "logo");
  queryParams.append("chain_ids", chainIds);
  queryParams.append("exclude_spam_tokens", "true");
  const url = buildSimduneUrl(`/v1/evm/balances/${address}`, queryParams);
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch balance: ${response.statusText}`);
  }
  const balanceData = await response.json();
  return balanceData;
}
async function fetchSimTokenBalance(walletAddress, tokenAddress, chainId) {
  if (!walletAddress) throw new Error("Wallet address is required");
  if (!tokenAddress) throw new Error("Token address is required");
  if (!chainId) throw new Error("Chain ID is required");
  const queryParams = new URLSearchParams();
  queryParams.append("chain_ids", chainId.toString());
  const url = buildSimduneUrl(`/v1/evm/balances/${walletAddress}/token/${tokenAddress}`, queryParams);
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch token balance: ${response.statusText}`);
  }
  const balanceData = await response.json();
  return balanceData;
}
async function fetchSimSvmBalance(address, chains, limit) {
  if (!address) throw new Error("Address is required");
  const queryParams = new URLSearchParams();
  if (chains && chains.length > 0) {
    queryParams.append("chains", chains.join(","));
  }
  if (limit) {
    queryParams.append("limit", limit.toString());
  }
  const url = buildSimduneUrl(`/beta/svm/balances/${address}`, queryParams.toString() ? queryParams : undefined);
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch SVM balance: ${response.statusText}`);
  }
  const balanceData = await response.json();
  return balanceData;
}
export function useSimBalance(address, chainIdsParam) {
  const $ = _c(8);
  let t0;
  let t1;
  if ($[0] !== address || $[1] !== chainIdsParam) {
    t0 = ["simBalance", address, chainIdsParam];
    t1 = () => {
      if (!address) {
        throw new Error("Address is required");
      }
      return fetchSimBalance(address, chainIdsParam || []);
    };
    $[0] = address;
    $[1] = chainIdsParam;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  const t2 = Boolean(address);
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
}
export function useSimSvmBalance(address, chains, limit) {
  const $ = _c(9);
  let t0;
  let t1;
  if ($[0] !== address || $[1] !== chains || $[2] !== limit) {
    t0 = ["svmBalance", address, chains, limit];
    t1 = () => {
      if (!address) {
        throw new Error("Address is required");
      }
      return fetchSimSvmBalance(address, chains, limit);
    };
    $[0] = address;
    $[1] = chains;
    $[2] = limit;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  const t2 = Boolean(address);
  let t3;
  if ($[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useQuery(t3);
}
/**
 * Hook to fetch a single token balance for a wallet.
 * @param walletAddress - The wallet address to fetch balance for
 * @param tokenAddress - The token contract address, or "native" for native token (ETH, etc.)
 * @param chainId - Chain ID to query (defaults to 1 for Ethereum mainnet)
 */
export function useSimTokenBalance(walletAddress, tokenAddress, chainId) {
  const $ = _c(9);
  let t0;
  let t1;
  if ($[0] !== chainId || $[1] !== tokenAddress || $[2] !== walletAddress) {
    t0 = ["simTokenBalance", walletAddress, tokenAddress, chainId];
    t1 = () => {
      if (!walletAddress || !tokenAddress || !chainId) {
        throw new Error("Missing required parameters");
      }
      return fetchSimTokenBalance(walletAddress, tokenAddress, chainId);
    };
    $[0] = chainId;
    $[1] = tokenAddress;
    $[2] = walletAddress;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  const t2 = Boolean(walletAddress) && Boolean(tokenAddress);
  let t3;
  if ($[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useQuery(t3);
}